import React, { useEffect, useRef, useState } from "react";
import { SECTION_TYPES, describeSectionRule } from "./sectionUtils";

const MAX_SECTIONS = 20;

function newSectionId(type, existing) {
  if (!existing.some((s) => s.id === type)) return type;
  return `${type}-${Date.now().toString(36)}`;
}

// Dialog for renaming, reordering, adding and removing My Tasks sections.
export default function SectionsEditor({
  sections,
  isDefault,
  onSave,
  onClose,
}) {
  const [draft, setDraft] = useState(() => sections.map((s) => ({ ...s })));
  const [addType, setAddType] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const update = (index, patch) =>
    setDraft((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );

  const move = (index, delta) =>
    setDraft((prev) => {
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(index + delta, 0, item);
      return next;
    });

  const remove = (index) =>
    setDraft((prev) => prev.filter((_, i) => i !== index));

  const add = () => {
    if (!addType) return;
    const preset = SECTION_TYPES.find((t) => t.type === addType);
    setDraft((prev) => [
      ...prev,
      {
        id: newSectionId(addType, prev),
        name: addType === "range" ? "Next 2 weeks" : preset.label,
        type: addType,
        ...(addType === "range" ? { from: 2, to: 14 } : {}),
      },
    ]);
    setAddType("");
  };

  const save = async (value) => {
    if (value) {
      if (value.some((s) => !s.name.trim())) {
        setError("Every section needs a name.");
        return;
      }
      if (value.length === 0) {
        setError("Keep at least one section.");
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save sections");
    } finally {
      setSaving(false);
    }
  };

  const usedTypes = new Set(draft.map((s) => s.type));
  const addOptions = SECTION_TYPES.filter(
    (t) => t.type === "range" || !usedTypes.has(t.type),
  );

  return (
    <div
      className="tk-modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="tk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tk-sections-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="tk-modal-head">
          <h2 id="tk-sections-title">Customize sections</h2>
          <button
            type="button"
            className="tk-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p className="tk-muted tk-modal-intro">
          Sections apply to both the list and the board. Each task appears in
          the first section, from the top, that matches it — so order matters.
        </p>

        <ol className="tk-section-editor">
          {draft.map((section, index) => (
            <li key={section.id}>
              <div className="tk-se-order">
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label={`Move ${section.name} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={() => move(index, 1)}
                  disabled={index === draft.length - 1}
                  aria-label={`Move ${section.name} down`}
                >
                  ↓
                </button>
              </div>
              <div className="tk-se-main">
                <input
                  className="tk-input"
                  value={section.name}
                  maxLength={60}
                  onChange={(e) => update(index, { name: e.target.value })}
                  aria-label="Section name"
                />
                {section.type === "range" ? (
                  <div className="tk-se-range">
                    Due from day
                    <input
                      type="number"
                      className="tk-input"
                      value={section.from}
                      onChange={(e) =>
                        update(index, { from: Number(e.target.value) })
                      }
                      aria-label="From day"
                    />
                    to day
                    <input
                      type="number"
                      className="tk-input"
                      value={section.to ?? ""}
                      placeholder="∞"
                      onChange={(e) =>
                        update(index, {
                          to:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                      aria-label="To day (leave empty for no end)"
                    />
                    <span className="tk-muted">0 = today, 1 = tomorrow</span>
                  </div>
                ) : (
                  <span className="tk-muted">
                    {describeSectionRule(section)}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="tk-icon-btn tk-icon-btn-sm"
                onClick={() => remove(index)}
                disabled={draft.length === 1}
                aria-label={`Remove ${section.name}`}
                title="Remove section"
              >
                ×
              </button>
            </li>
          ))}
        </ol>

        {draft.length < MAX_SECTIONS && (
          <div className="tk-se-add">
            <select
              className="tk-input"
              value={addType}
              onChange={(e) => setAddType(e.target.value)}
              aria-label="Section to add"
            >
              <option value="">Add a section…</option>
              {addOptions.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label} — {t.hint}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={add}
              disabled={!addType}
            >
              Add
            </button>
          </div>
        )}

        {error && <p className="tk-error">{error}</p>}

        <div className="tk-modal-actions">
          <button
            type="button"
            className="tk-link-btn"
            onClick={() => save(null)}
            disabled={saving || isDefault}
          >
            Reset to default
          </button>
          <span className="tk-modal-actions-right">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save(draft)}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save sections"}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
